/**
 * Every fee, limit, contact and airline list a Help Center article states
 * comes from here, and everything here comes from the constants the app and
 * the concierge's knowledge base already read. Articles write `{{TOKEN}}`
 * instead of the number, so a policy change can never leave an article
 * behind (help-content.test.js fails on a literal fee in article text).
 *
 * Relative imports only: the build-time pre-renderer (scripts/
 * prerender-help.mjs) imports this in plain Node.
 */
import {
  SKIP_PAYMENT_FEE_CENTS,
  EXTENSION_FEE_CENTS,
  REINSTATEMENT_FEE_CENTS,
  SCHEDULE_DATE_CHANGE_FEE_CENTS,
  SCHEDULE_DATE_CHANGE_CUTOFF_DAYS,
  PAYMENT_EXTENSION_FEE_TIERS,
  PAYMENT_EXTENSION_MAX_DAYS,
  PAYMENT_EXTENSIONS_PER_TRIP,
  formatCentsAsDollars,
} from '../../constants/paymentPolicy.js';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_NUMBER, SECURITY_EMAIL } from '../../constants/support.js';
import {
  MILES_TRIP_CENTS_PER_MILE,
  MILES_GIFT_CARD_CENTS_PER_MILE,
  MILES_CHECKOUT_MIN,
  MILES_MIN_PLAN_CENTS,
} from '../../../supabase/functions/_shared/miles-redemption.js';
import { MILES_TILE_MINIMUMS } from '../../constants/milesTiles.js';
import { CHANGE_FLIGHT_ALLOWED_AIRLINE_NAMES } from '../../utils/changeFlightEligibility.js';
import { SEATMAP_UNSUPPORTED_AIRLINE_NAMES } from '../../utils/seatPurchaseEligibility.js';
import { HELP_CENTER_TRAVEL_INSURANCE_ANSWER } from '../../constants/fayeInsuranceCopy.js';
import { FAYE_MIN_BASE_FARE, FAYE_RULES } from '../../../supabase/functions/_shared/faye-pricing.js';

/** "A, B and C" */
export function listOf(names) {
  const items = (names || []).filter(Boolean);
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Fewest days before departure a trip can be booked: search and checkout
 * start here, and the deposit calculator offers no plan below it. Not an
 * exported constant in the calculator, so help-content.test.js pins it to
 * the calculator's behaviour (21 days → no plan, 22 → plan).
 */
export const PLAN_MIN_DAYS = 22;

/**
 * The airlines whose refundable fares can be booked with Defera Zero. Fare
 * refundability decides eligibility in the app; this list is the one the
 * knowledge base gives customers, and help-content.test.js holds the two
 * together.
 */
export const ZERO_AIRLINE_NAMES = Object.freeze(['American', 'United', 'Southwest', 'JetBlue', 'Virgin Atlantic']);

export const HELP_TOKENS = Object.freeze({
  SKIP_FEE: formatCentsAsDollars(SKIP_PAYMENT_FEE_CENTS),
  EXTENSION_FEE: formatCentsAsDollars(EXTENSION_FEE_CENTS),
  REINSTATEMENT_FEE: formatCentsAsDollars(REINSTATEMENT_FEE_CENTS),
  DATE_CHANGE_FEE: formatCentsAsDollars(SCHEDULE_DATE_CHANGE_FEE_CENTS),
  DATE_CHANGE_CUTOFF_DAYS: String(SCHEDULE_DATE_CHANGE_CUTOFF_DAYS),
  // Moving a payment ("Can't make this payment?"): the fee by how far it moves.
  MOVE_FEE_SHORT: formatCentsAsDollars(PAYMENT_EXTENSION_FEE_TIERS[0].feeCents),
  MOVE_FEE_SHORT_DAYS: String(PAYMENT_EXTENSION_FEE_TIERS[0].maxDays),
  MOVE_FEE_LONG: formatCentsAsDollars(PAYMENT_EXTENSION_FEE_TIERS[1].feeCents),
  MOVE_MAX_DAYS: String(PAYMENT_EXTENSION_MAX_DAYS),
  MOVES_PER_TRIP: String(PAYMENT_EXTENSIONS_PER_TRIP),
  PLAN_MIN_DAYS: String(PLAN_MIN_DAYS),
  SECURITY_EMAIL,
  SUPPORT_PHONE: SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL: SUPPORT_PHONE_NUMBER,
  MILES_TRIP_VALUE: `${MILES_TRIP_CENTS_PER_MILE}¢`,
  MILES_GIFT_CARD_VALUE: `${MILES_GIFT_CARD_CENTS_PER_MILE}¢`,
  MILES_CHECKOUT_MIN: MILES_CHECKOUT_MIN.toLocaleString('en-US'),
  MILES_MIN_PLAN: formatCentsAsDollars(MILES_MIN_PLAN_CENTS),
  MILES_SEATS_TILE: MILES_TILE_MINIMUMS.seats.toLocaleString('en-US'),
  MILES_BAGS_TILE: MILES_TILE_MINIMUMS.baggage.toLocaleString('en-US'),
  MILES_UPGRADE_TILE: MILES_TILE_MINIMUMS.cabin.toLocaleString('en-US'),
  ZERO_AIRLINES: listOf(ZERO_AIRLINE_NAMES),
  CHANGE_FLIGHT_AIRLINES: listOf(CHANGE_FLIGHT_ALLOWED_AIRLINE_NAMES),
  SEAT_MAP_UNSUPPORTED_AIRLINES: listOf(SEATMAP_UNSUPPORTED_AIRLINE_NAMES),
  // The approved insurance answer, word for word: faye-kb-parity.test.js
  // holds it to the knowledge base, so the article quotes it, never retells it.
  TRAVEL_INSURANCE_ANSWER: HELP_CENTER_TRAVEL_INSURANCE_ANSWER,
  // The same eligibility numbers the approved answer interpolates.
  INSURANCE_MIN_FARE: `$${FAYE_MIN_BASE_FARE.toLocaleString('en-US')}`,
  INSURANCE_CUTOFF_DAYS: String(FAYE_RULES.fullCoverageMinDaysBeforeDeparture),
});

/** Contact details that must only ever appear through their token. */
export const TOKENIZED_CONTACTS = Object.freeze([
  { key: 'SECURITY_EMAIL', pattern: new RegExp(SECURITY_EMAIL.replace(/[.@]/g, (c) => `\\${c}`), 'i') },
  // The last seven digits, however they're spaced or punctuated.
  { key: 'SUPPORT_PHONE', pattern: (() => { const d = SUPPORT_PHONE_DISPLAY.replace(/\D/g, '').slice(-7); return new RegExp(`${d.slice(0, 3)}[\\s.-]?${d.slice(3)}`); })() },
]);

/** Dollar amounts that must only ever appear through their token. */
export const TOKENIZED_AMOUNTS = Object.freeze(
  ['SKIP_FEE', 'EXTENSION_FEE', 'REINSTATEMENT_FEE', 'DATE_CHANGE_FEE', 'MILES_MIN_PLAN', 'INSURANCE_MIN_FARE']
    .map((key) => ({ key, value: HELP_TOKENS[key] })),
);

const TOKEN_PATTERN = /\{\{\s*([A-Z0-9_]+)\s*\}\}/g;

/** The token names a text uses. */
export function tokensIn(text) {
  return [...String(text || '').matchAll(TOKEN_PATTERN)].map((m) => m[1]);
}

/** Replace every `{{TOKEN}}`. An unknown token throws: an article must never
 *  ship a blank where a fee should be. */
export function resolveTokens(text) {
  return String(text || '').replace(TOKEN_PATTERN, (_, name) => {
    if (!Object.prototype.hasOwnProperty.call(HELP_TOKENS, name)) {
      throw new Error(`Unknown help token {{${name}}}`);
    }
    return HELP_TOKENS[name];
  });
}
